import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRightLeft, Clock, User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { format, formatDistanceToNow } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TransferRecord {
  id: string;
  ticketId: string;
  fromTeamName: string | null;
  toTeamName: string;
  reason: string | null;
  actorName: string | null;
  createdAt: string;
}

export function TeamTransferHistory() {
  const { activeShop } = useAuth();

  const { data: transfers, isLoading } = useQuery({
    queryKey: ['team-transfer-history', activeShop?.id],
    queryFn: async () => {
      if (!activeShop?.id) return [];

      // Get team transfer audit logs
      const { data: logs, error } = await supabase
        .from('audit_log')
        .select(`
          id,
          object_id,
          from_team_id,
          to_team_id,
          reason,
          actor_user_id,
          created_at
        `)
        .eq('shop_id', activeShop.id)
        .eq('action', 'team_transfer')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      if (!logs?.length) return [];

      // Get team names
      const teamIds = new Set<string>();
      logs.forEach(log => {
        if (log.from_team_id) teamIds.add(log.from_team_id);
        if (log.to_team_id) teamIds.add(log.to_team_id);
      });

      const { data: teams } = await supabase
        .from('teams')
        .select('id, name')
        .in('id', Array.from(teamIds));

      const teamMap = new Map(teams?.map(t => [t.id, t.name]) || []);

      // Get actor names
      const actorIds = new Set(logs.map(l => l.actor_user_id).filter(Boolean));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .in('id', Array.from(actorIds) as string[]);

      const actorMap = new Map(
        profiles?.map(p => [p.id, p.display_name || p.email || 'Unknown']) || []
      );

      return logs.map(log => ({
        id: log.id,
        ticketId: log.object_id || '',
        fromTeamName: log.from_team_id ? teamMap.get(log.from_team_id) || 'Unknown Team' : null,
        toTeamName: log.to_team_id ? teamMap.get(log.to_team_id) || 'Unknown Team' : 'Unknown Team',
        reason: log.reason,
        actorName: log.actor_user_id ? actorMap.get(log.actor_user_id) || 'Unknown' : null,
        createdAt: log.created_at,
      })) as TransferRecord[];
    },
    enabled: !!activeShop?.id,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Transfer History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5" />
          Team Transfer History
        </CardTitle>
        <CardDescription>
          Recent ticket transfers between teams
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!transfers?.length ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No team transfers recorded yet
          </p>
        ) : (
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-3">
              {transfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      {transfer.fromTeamName ? (
                        <>
                          <Badge variant="outline">{transfer.fromTeamName}</Badge>
                          <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" />
                          <Badge>{transfer.toTeamName}</Badge>
                        </>
                      ) : (
                        <>
                          <span className="text-muted-foreground">Assigned to</span>
                          <Badge>{transfer.toTeamName}</Badge>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{formatDistanceToNow(new Date(transfer.createdAt), { addSuffix: true })}</span>
                    </div>
                  </div>

                  {transfer.reason && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      "{transfer.reason}"
                    </p>
                  )}

                  <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                    <User className="h-3 w-3" />
                    <span>{transfer.actorName || 'System'}</span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
